import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useRef, useState } from "react";
import type {
  ReferenceDatumInput,
  ReferenceDatumInputVal,
} from "../../../../../api-requests/reference-data";
import { appLog } from "../../../../../context";
import type { SchemaField } from "../../../../../helpers/format-databases";
import type { ReferenceDatum } from "../../../../../helpers/format-reference-data";
import { transformToFormDataIfFileField } from "../../../../../helpers/transform-to-form-data";
import { useAppStore } from "../../../../../store/app.store";
import type { ModalDialogProps } from "../../../../shared/global-tools/dialog-system";
import { clearToast, showToast } from "../../../../shared/global-tools/toast-system";
import type { FileInputHandle } from "../../SchemaFieldTypeInputs/FileInput/FileInput";
import SchemaFieldInput from "../../SchemaFieldTypeInputs/SchemaFieldInput";

import "./ReferenceDatumFormDialog.scss";

export interface ReferenceDatumFormDialogProps extends ModalDialogProps<void> {
  closeDialog: () => void;
  parameter: ReferenceDatumFormDialogParameter;
}

export interface ReferenceDatumFormDialogParameter {
  referenceId: number;
  schema: SchemaField[];
  referenceDatum?: ReferenceDatum;
  save: (input: ReferenceDatumInput | FormData) => Promise<void>;
  header: string;
  successMessage: string;
  errorMessage: string;
}

export default function makeReferenceDatumFormDialog({
  closeDialog,
  onCancelDialog,
  parameter: { save, referenceId, referenceDatum, schema, header, successMessage, errorMessage },
}: ReferenceDatumFormDialogProps) {
  const wrapAsync = useWrapAsync();
  const [loading, setLoading] = useState(false);
  const [inputValues, setInputValues] = useState<ReferenceDatumInput>(new Map());
  const [errorStatus, setErrorStatus] = useState<Map<string, boolean>>(new Map());
  const referenceDataById = useAppStore((state) => state.referenceDataById);
  const setReferenceDataById = useAppStore((state) => state.setReferenceDataById);
  const fileInputRef = useRef<FileInputHandle>(null);

  onCancelDialog(() => {
    clearToast();
    return true;
  });

  const disabledButton = () => {
    for (const [, status] of errorStatus) {
      if (status) {
        return true;
      }
    }
    return false;
  };
  const validate = wrapAsync(async () => {
    if (fileInputRef.current && !fileInputRef.current.checkInput()) return;
    setLoading(true);
    if (inputValues.size === 0) return;
    try {
      const values = transformToFormDataIfFileField(inputValues, schema);
      await save(values);
      setLoading(false);
      showToast(successMessage, { severity: "success" });
      closeDialog();

      const oldReferenceDataById = new Map(referenceDataById);
      oldReferenceDataById.set(referenceId, undefined);
      setReferenceDataById(oldReferenceDataById);
    } catch (error) {
      appLog.error(error);
      showToast(errorMessage, { severity: "error", sticky: true });
    }
  });
  const onChange = ({ name, value }: { name: string; value?: ReferenceDatumInputVal }) => {
    const oldValues: ReferenceDatumInput = new Map(inputValues);
    oldValues.set(name, value);
    setInputValues(oldValues);
  };

  useEffect(() => {
    if (!referenceDatum) return;
    const oldValues: ReferenceDatumInput = new Map(inputValues);
    for (const f of schema) {
      oldValues.set(f.name, referenceDatum[f.name]);
    }
    setInputValues(oldValues);
  }, []);

  const content = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        validate();
      }}
    >
      <div className="ReferenceDatumFormDialog-content">
        {schema.map((field) => (
          <SchemaFieldInput
            key={field.name}
            field={field}
            value={inputValues.get(field.name)}
            forwardedRef={fileInputRef}
            onChange={onChange}
            onError={(val) => {
              const oldErrorStatus = new Map(errorStatus);
              oldErrorStatus.set(field.name, val);
              setErrorStatus(oldErrorStatus);
            }}
          />
        ))}
      </div>

      <div
        style={{
          marginTop: "30px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          className="Btn secondary"
          label="Annuler"
          onClick={() => {
            closeDialog();
            clearToast();
          }}
        />
        <Button
          type="submit"
          className="Btn primary"
          label="Valider"
          disabled={disabledButton()}
          loading={loading}
        />
      </div>
    </form>
  );

  return {
    header,
    content,
  };
}
